import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'
import { rotateVector } from '@/lib/transforms/geometry'
import { type AnyRecord, asRecord, finiteNumber } from './tolerant-read' /**
 * Reads a photo fill placed inside a shape (kindId 1, 2, 9 closed, 10) from `content.image` —
 * the same `<XX><IM X Y W H .../></XX>` structure the legacy app reads/writes. Geometry fields
 * are flat on `image` itself (no nested `base` — matches the backend's `ImageElement` Go
 * struct). `width/height` are the actual rendered size — required for legacy to display the
 * photo correctly (omitting them makes legacy fall back to a fixed 300dpi native-pixel size
 * unrelated to the shape's frame, confirmed by direct testing) — stashed as
 * `persistedRenderedWidth/Height` and used as-is (`scale` defaults to `1`) until the async
 * natural-size correction can back out a real `scale`.
 *
 * `x/y` are NOT `offsetX`/`offsetY` directly when `rotation` is nonzero — see
 * `buildShapeImageFillContent` in stowed-element-builder.ts for why legacy's rotate-in-place
 * pivot differs from Larry's rotate-around-frame-center pivot. `x/y` must be un-rotated by
 * `-rotation` to recover the raw pan offset Larry's own renderer expects.
 */
export function readShapeFillImage(contentDecoded?: AnyRecord): ShapePhotoFill | undefined {
  const content = contentDecoded
    ? (asRecord(contentDecoded.content) ?? asRecord(contentDecoded.Content))
    : undefined
  const image = content ? (asRecord(content.image) ?? asRecord(content.Image)) : undefined

  if (!image) return undefined

  const imageId = image.imageId ?? image.image_id ?? image.ImageId
  const imageType = image.imageType ?? image.image_type ?? image.ImageType
  const pezId = image.pezId ?? image.pez_id ?? image.PezId
  const previewUrl = image.imagePreviewUrl ?? image.image_preview_url ?? image.ImagePreviewUrl
  const displayName = image.imageDisplayName ?? image.image_display_name ?? image.ImageDisplayName

  // A photo fill carries its real id in `imageId` and leaves `pezId` empty; a ClikArt fill is the
  // opposite (`imageId: ''`, real id in `pezId` — see build-clikart-shape-fill.ts). Requiring only
  // `imageId` here silently discarded every ClikArt fill on load/reload (WMPCM-11014 regression):
  // the shape came back as an empty transparent rectangle, `photoFill` dropped entirely.
  const hasImageId = typeof imageId === 'string' && imageId !== ''
  const hasPezId = typeof pezId === 'string' && pezId !== ''

  if (!hasImageId && !hasPezId) return undefined

  // Rotation of the photo CONTENT within its frame (crop mode) — independent of the shape's own
  // rotation. See `buildShapeImageFillContent` in stowed-element-builder.ts.
  const rotation = finiteNumber(image.rotation ?? image.Rotation)
  const rawOffsetX = finiteNumber(image.x ?? image.X) ?? 0
  const rawOffsetY = finiteNumber(image.y ?? image.Y) ?? 0
  const { x: offsetX, y: offsetY } = rotateVector(
    { x: rawOffsetX, y: rawOffsetY },
    -(rotation ?? 0)
  )
  const rawWidth = finiteNumber(image.width ?? image.Width) ?? undefined
  const rawHeight = finiteNumber(image.height ?? image.Height) ?? undefined
  // Legacy's own encoding of a horizontally/vertically-flipped image
  // (`ODImage.flipx`/`.flipy`) is a negative width/height, not a dedicated flag — see
  // `ShapePhotoFill.flippedX`/`.flippedY` doc comments.
  const flippedX = rawWidth != null && rawWidth < 0
  const flippedY = rawHeight != null && rawHeight < 0
  const persistedRenderedWidth = rawWidth != null ? Math.abs(rawWidth) : undefined
  const persistedRenderedHeight = rawHeight != null ? Math.abs(rawHeight) : undefined

  return {
    imageId: typeof imageId === 'string' ? imageId : '',
    imageType: typeof imageType === 'string' ? imageType : 'Candid',
    pezId: typeof pezId === 'string' ? pezId : '',
    previewUrl: typeof previewUrl === 'string' ? previewUrl : '',
    displayName: typeof displayName === 'string' ? displayName : '',
    offsetX,
    offsetY,
    // Placeholder until the async natural-size correction resolves a real zoom multiplier.
    scale: 1,
    ...(flippedX ? { flippedX } : {}),
    ...(flippedY ? { flippedY } : {}),
    ...(persistedRenderedWidth !== undefined ? { persistedRenderedWidth } : {}),
    ...(persistedRenderedHeight !== undefined ? { persistedRenderedHeight } : {}),
    ...(rotation != null ? { rotation } : {}),
  }
}
